import { useEffect, useMemo, useRef, useState } from "react";
import type { IScannerControls } from "@zxing/browser";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Camera,
  Check,
  CheckCircle2,
  ClipboardPlus,
  LogOut,
  PackageCheck,
  QrCode,
  Search,
  Save,
  ShieldCheck,
  Stethoscope,
  Users,
  X,
} from "lucide-react";
import { api } from "../lib/api";
import { beginLogin, getGroups, signOut, type Session } from "../lib/auth";
import type { RuntimeConfig } from "../lib/config";
import { allowedTabsForGroups, type StaffTab } from "../lib/roles";
import { isMegaHealthFairQrCredential, normalizeQrCredential } from "../lib/qr";
import {
  ageGroupOptions, childAgeGroupOptions, childCountOptions, emptyChild, ethnicityOptions, raceOptions,
  registrationTypes, relationshipOptions, requiredChildCount, sexAtBirthOptions,
  type ChildRegistration, type RegistrationFormData,
} from "../lib/registration";
import { services } from "../data/content";

type Dashboard = {
  registrations: number;
  checkedIn: number;
  stationVisits: number;
  completedVisits: number;
  distributions: number;
  surveys: number;
  openIncidents: number;
  serviceDemand: Array<{ name: string; count: number }>;
};

type Attendee = {
  attendeeId: string;
  displayName: string;
  phoneLast4: string;
  preferredLanguage: string;
  preferredCheckInTime: string;
  selectedServices: string[];
  backToSchoolInterest: boolean;
  foodBagsInterest: boolean;
  checkInStatus?: string;
};

type Visit = {
  visitId: string;
  attendeeId: string;
  attendeeName: string;
  station: string;
  status: string;
  arrivalTime: string;
};

type Slot = {
  id: string;
  label: string;
  available: number;
  status: string;
};

type RegistrationRecord = RegistrationFormData & {
  attendeeId: string;
  preferredCheckInTime: string;
  registrationStatus: string;
  checkInStatus: string;
  qrStatus: string;
  createdAt: string;
  updatedAt: string;
};

export function StaffPortal({ config, session }: { config: RuntimeConfig; session: Session | null }) {
  const groups = getGroups(session);
  const allowedTabs = allowedTabsForGroups(groups);
  const [tab, setTab] = useState<StaffTab>(() => allowedTabs[0] || "incidents");

  if (!session) {
    return (
      <main className="staff-login">
        <div className="staff-login-card">
          <img className="staff-login-logo" src="/church-logo-navy.png" alt="Redeemers Church of Christ — The Ark of Love" />
          <p className="eyebrow">Secure operations</p>
          <h1>Health fair staff portal</h1>
          <p>Authorized check-in teams, providers, station leads, distribution staff, and administrators sign in through AWS Cognito.</p>
          <button className="button button-primary" onClick={() => beginLogin(config)}>Sign in securely</button>
          <div className="privacy-note"><ShieldCheck size={20} /><span>Role-based access · strong passwords · encrypted audit trail</span></div>
          <a href="/">Return to public registration</a>
        </div>
      </main>
    );
  }

  const userName = String(session.claims.name || session.claims.email || "Staff member");
  return (
    <div className="portal-shell">
      <aside className="portal-sidebar">
        <a className="portal-brand" href="/"><img className="church-logo" src="/church-logo-white.png" alt="Redeemers Church of Christ — The Ark of Love" /><strong>Mega Health Fair</strong></a>
        <nav>
          {allowedTabs.includes("dashboard") && <NavButton active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={<BarChart3 />} label="Command center" />}
          {allowedTabs.includes("registrants") && <NavButton active={tab === "registrants"} onClick={() => setTab("registrants")} icon={<Users />} label="Registrant records" />}
          {allowedTabs.includes("checkin") && <NavButton active={tab === "checkin"} onClick={() => setTab("checkin")} icon={<QrCode />} label="Check-in desk" />}
          {allowedTabs.includes("stations") && <NavButton active={tab === "stations"} onClick={() => setTab("stations")} icon={<Stethoscope />} label="Medical stations" />}
          {allowedTabs.includes("distribution") && <NavButton active={tab === "distribution"} onClick={() => setTab("distribution")} icon={<PackageCheck />} label="Distribution" />}
          {allowedTabs.includes("incidents") && <NavButton active={tab === "incidents"} onClick={() => setTab("incidents")} icon={<AlertTriangle />} label="Incident log" />}
        </nav>
        <div className="sidebar-user">
          <span>{userName.slice(0, 1).toUpperCase()}</span>
          <div><strong>{userName}</strong><small>{groups.join(" · ") || "Authorized staff"}</small></div>
          <button aria-label="Sign out" onClick={() => signOut(config)}><LogOut size={18} /></button>
        </div>
      </aside>
      <main className="portal-main">
        <header className="portal-header"><div><p>Saturday, August 22, 2026</p><h1>{tabTitles[tab]}</h1></div><div className="live-pill"><span /> Event systems ready</div></header>
        {tab === "dashboard" && <DashboardView config={config} session={session} />}
        {tab === "registrants" && <RegistrantRecordsView config={config} session={session} onReturnToCheckInDesk={() => setTab("checkin")} />}
        {tab === "checkin" && <CheckInView config={config} session={session} />}
        {tab === "stations" && <StationView config={config} session={session} />}
        {tab === "distribution" && <DistributionView config={config} session={session} groups={groups} />}
        {tab === "incidents" && <IncidentView config={config} session={session} />}
      </main>
    </div>
  );
}

const tabTitles: Record<StaffTab, string> = {
  dashboard: "Command center",
  registrants: "Registrant records",
  checkin: "Event-day check-in",
  stations: "Medical station queue",
  distribution: "Family support distribution",
  incidents: "Incident and exception log",
};

function NavButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return <button className={active ? "active" : ""} onClick={onClick}>{icon}<span>{label}</span></button>;
}

function DashboardView({ config, session }: { config: RuntimeConfig; session: Session }) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<Dashboard>(config, "/app/dashboard", {}, session).then(setData).catch((caught) => setError(caught.message));
  }, [config, session]);

  if (error) return <Notice kind="error" text={error} />;
  if (!data) return <Loading />;
  const attendanceRate = data.registrations ? Math.round((data.checkedIn / data.registrations) * 100) : 0;
  const maxDemand = Math.max(...data.serviceDemand.map((item) => item.count), 1);
  return (
    <div className="portal-content">
      <div className="metric-grid">
        <Metric icon={<Users />} label="Registrations" value={data.registrations} trend="Confirmed attendees" />
        <Metric icon={<CheckCircle2 />} label="Checked in" value={data.checkedIn} trend={`${attendanceRate}% attendance`} />
        <Metric icon={<Activity />} label="Station visits" value={data.stationVisits} trend={`${data.completedVisits} completed`} />
        <Metric icon={<PackageCheck />} label="Distributions" value={data.distributions} trend="Food + school support" />
      </div>
      <div className="dashboard-grid">
        <section className="panel service-demand">
          <div className="panel-heading"><div><p className="eyebrow">Live demand</p><h2>Requested services</h2></div><BarChart3 /></div>
          <div className="bar-list">
            {data.serviceDemand.slice(0, 8).map((item) => <div key={item.name}><span>{item.name}</span><div><i style={{ width: `${(item.count / maxDemand) * 100}%` }} /></div><strong>{item.count}</strong></div>)}
            {!data.serviceDemand.length && <p className="muted">Service demand will appear after registrations begin.</p>}
          </div>
        </section>
        <section className="panel operations-panel">
          <div className="panel-heading"><div><p className="eyebrow">Operations</p><h2>Readiness signals</h2></div><ShieldCheck /></div>
          <StatusRow label="Registration service" value="Operational" good />
          <StatusRow label="Survey responses" value={String(data.surveys)} good />
          <StatusRow label="Open incidents" value={String(data.openIncidents)} good={data.openIncidents === 0} />
          <StatusRow label="Clinical access" value="Restricted" good />
        </section>
      </div>
    </div>
  );
}

function RegistrantRecordsView({ config, session, onReturnToCheckInDesk }: { config: RuntimeConfig; session: Session; onReturnToCheckInDesk: () => void }) {
  const [credential, setCredential] = useState("");
  const [record, setRecord] = useState<RegistrationRecord | null>(null);
  const [draft, setDraft] = useState<RegistrationRecord | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [changeReason, setChangeReason] = useState("");
  const [message, setMessage] = useState("");
  const [messageKind, setMessageKind] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInResult, setCheckInResult] = useState<"CHECKED_IN" | "ALREADY_CHECKED_IN" | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const canCheckIn = getGroups(session).some((group) => group === "EventAdmins" || group === "CheckInStaff");
  const hasUnsavedChanges = useMemo(() => Boolean(record && draft && JSON.stringify(record) !== JSON.stringify(draft)), [record, draft]);

  useEffect(() => {
    api<{ slots: Slot[] }>(config, "/public/slots").then((data) => setSlots(data.slots)).catch(() => setSlots([]));
  }, [config]);

  function update<K extends keyof RegistrationRecord>(key: K, value: RegistrationRecord[K]) {
    setDraft((current) => current ? { ...current, [key]: value } : current);
    setCheckInResult(null);
    setMessage("");
  }

  function setRegistrantChildCount(value: string) {
    const count = requiredChildCount(value);
    setDraft((current) => current ? { ...current, numberOfChildren: value, relationshipToChild: count ? current.relationshipToChild : "", children: Array.from({ length: count }, (_, index) => current.children[index] || emptyChild(index + 1)), additionalChildrenInformation: value === "More than 5" ? current.additionalChildrenInformation : "" } : current);
    setCheckInResult(null);
    setMessage("");
  }

  function updateRegistrantChild(index: number, key: keyof ChildRegistration, value: string | number) {
    setDraft((current) => current ? { ...current, children: current.children.map((child, childIndex) => childIndex === index ? { ...child, [key]: value } : child) } : current);
    setCheckInResult(null);
    setMessage("");
  }

  function toggleService(service: string) {
    if (!draft) return;
    update("selectedServices", draft.selectedServices.includes(service)
      ? draft.selectedServices.filter((item) => item !== service)
      : [...draft.selectedServices, service]);
  }

  async function lookup(value = credential) {
    const token = normalizeQrCredential(value);
    setCredential(token);
    setLoading(true); setMessage(""); setRecord(null); setDraft(null); setCheckInResult(null);
    try {
      const data = await api<{ registration: RegistrationRecord }>(config, "/app/registrations/lookup", { method: "POST", body: JSON.stringify({ token }) }, session);
      setRecord(data.registration); setDraft(data.registration); setChangeReason("");
      setMessageKind("success"); setMessage("Registration loaded. Review or update the information below.");
    } catch (caught) {
      setMessageKind("error"); setMessage(caught instanceof Error ? caught.message : "Unable to load the registration.");
    } finally { setLoading(false); }
  }

  function acceptScannedCredential(value: string) {
    const token = normalizeQrCredential(value);
    setScannerOpen(false);
    void lookup(token);
  }

  async function save() {
    if (!draft || !record) return;
    setSaving(true); setMessage("");
    try {
      const data = await api<{ status: string; registration: RegistrationRecord }>(config, `/app/registrations/${encodeURIComponent(draft.attendeeId)}`, {
        method: "PATCH",
        body: JSON.stringify({ ...draft, token: normalizeQrCredential(credential), changeReason }),
      }, session);
      setRecord(data.registration); setDraft(data.registration); setChangeReason("");
      setMessageKind("success"); setMessage(data.status === "UNCHANGED" ? "No registration changes were detected." : "Registration updated and the change was added to the audit trail.");
    } catch (caught) {
      setMessageKind("error"); setMessage(caught instanceof Error ? caught.message : "Unable to update the registration.");
    } finally { setSaving(false); }
  }

  async function checkInRegistrant() {
    if (!draft || hasUnsavedChanges) return;
    setCheckingIn(true); setMessage(""); setCheckInResult(null);
    try {
      const data = await api<{ attendee: Attendee; status: "CHECKED_IN" | "ALREADY_CHECKED_IN" }>(config, "/app/checkin", {
        method: "POST",
        body: JSON.stringify({ token: normalizeQrCredential(credential), counter: "Issue Resolution" }),
      }, session);
      const checkedInRecord = { ...draft, checkInStatus: "CHECKED_IN", qrStatus: "USED" };
      setRecord(checkedInRecord); setDraft(checkedInRecord); setCheckInResult(data.status);
    } catch (caught) {
      setMessageKind("error"); setMessage(caught instanceof Error ? caught.message : "Check-in failed.");
    } finally { setCheckingIn(false); }
  }

  return (
    <div className="portal-content registrant-workspace">
      <section className="panel action-panel registrant-lookup">
        <p className="eyebrow">Secure lookup</p><h2>Scan registration QR</h2>
        <p className="workflow-copy">Active and already-used QR codes can open the registrant’s complete registration record.</p>
        <button className="button button-secondary wide scan-button" type="button" onClick={() => setScannerOpen(true)}><Camera size={19} /> Scan registration QR code</button>
        <div className="scan-divider"><span>or enter manually</span></div>
        <label>QR credential or confirmation link<input className="large-input" placeholder="MHF2026-…" value={credential} onChange={(event) => setCredential(event.target.value)} /></label>
        <button className="button button-primary wide" type="button" disabled={!credential || loading} onClick={() => lookup()}><Search size={18} /> {loading ? "Loading…" : "Open registration"}</button>
        {record && <div className="record-summary"><span>{record.qrStatus === "USED" ? "Checked-in QR" : "Active QR"}</span><strong>{record.attendeeId}</strong><small>{record.checkInStatus === "CHECKED_IN" ? "Attendee checked in" : "Not checked in"}</small></div>}
        {message && <Notice kind={messageKind} text={message} />}
        <div className="privacy-note"><ShieldCheck size={20} /><span>Full registration access is logged with the signed-in staff identity.</span></div>
      </section>

      {!draft ? (
        <section className="panel registrant-editor"><div className="empty-state"><QrCode size={34} /><h3>Scan a registrant’s QR code</h3><p>Their registration profile, purpose of visit, arrival time, support interests, and communication preferences will appear here.</p></div></section>
      ) : (
        <section className="panel registrant-editor">
          <div className="editor-heading"><div><p className="eyebrow">Complete registration</p><h2>{draft.firstName} {draft.lastName}</h2></div><div className="record-badges"><span>{draft.registrationStatus}</span>{draft.checkInStatus === "CHECKED_IN" && <span className="checked">Checked in</span>}</div></div>
          <div className="editor-section"><h3>Personal and demographic information</h3><div className="form-grid compact-form">
            <label>First name<input required value={draft.firstName} onChange={(event) => update("firstName", event.target.value)} /></label>
            <label>Last name<input required value={draft.lastName} onChange={(event) => update("lastName", event.target.value)} /></label>
            <label>Phone number<input required type="tel" value={draft.phone} onChange={(event) => update("phone", event.target.value)} /></label>
            <label>Email address<input required type="email" value={draft.email} onChange={(event) => update("email", event.target.value)} /></label>
            <label>Date of birth (optional)<input type="date" value={draft.dateOfBirth} onChange={(event) => update("dateOfBirth", event.target.value)} /></label>
            <label>Age group<select value={draft.ageGroup} onChange={(event) => update("ageGroup", event.target.value)}>{ageGroupOptions.map((item) => <option key={item}>{item}</option>)}</select></label>
            {draft.ageGroup === "Child" && requiredChildCount(draft.numberOfChildren) === 0 && <><label>Consenting adult full name<input value={draft.consentingAdultFullName} onChange={(event) => update("consentingAdultFullName", event.target.value)} /></label><label>Consenting adult contact number<input type="tel" value={draft.consentingAdultContactNumber} onChange={(event) => update("consentingAdultContactNumber", event.target.value)} /></label></>}
            <label>Sex at birth<select value={draft.sexAtBirth} onChange={(event) => update("sexAtBirth", event.target.value)}>{sexAtBirthOptions.map((item) => <option key={item}>{item}</option>)}</select></label>
            <label>Race<select value={draft.race} onChange={(event) => update("race", event.target.value)}><option value="">Select race</option>{raceOptions.map((item) => <option key={item}>{item}</option>)}</select></label>
            {draft.race === "Other" && <label>If other race, specify<input value={draft.otherRace} onChange={(event) => update("otherRace", event.target.value)} /></label>}
            <label>Ethnicity (optional)<select value={draft.ethnicity} onChange={(event) => update("ethnicity", event.target.value)}><option value="">Select ethnicity</option>{ethnicityOptions.map((item) => <option key={item}>{item}</option>)}</select></label>
            {draft.ethnicity === "Other (Please Specify)" && <label>If other ethnicity, specify<input value={draft.otherEthnicity} onChange={(event) => update("otherEthnicity", event.target.value)} /></label>}
            <label>Preferred language<select value={draft.preferredLanguage} onChange={(event) => update("preferredLanguage", event.target.value)}><option value="EN">English</option><option value="ES">Spanish</option><option value="FR">French</option></select></label>
          </div></div>

          <div className="editor-section"><h3>Household and child registration</h3><div className="form-grid compact-form">
            <label>Registration type<select value={draft.registrationType} onChange={(event) => update("registrationType", event.target.value)}>{registrationTypes.map((item) => <option key={item}>{item}</option>)}</select></label>
            <label>Number of children<select value={draft.numberOfChildren} onChange={(event) => setRegistrantChildCount(event.target.value)}>{childCountOptions.map((item) => <option key={item}>{item}</option>)}</select></label>
            {requiredChildCount(draft.numberOfChildren) > 0 && <label>Relationship to child<select value={draft.relationshipToChild} onChange={(event) => update("relationshipToChild", event.target.value)}><option value="">Select relationship</option>{relationshipOptions.map((item) => <option key={item}>{item}</option>)}</select></label>}
            <label>Total household members attending<input type="number" min="1" step="1" value={draft.totalHouseholdAttending} onChange={(event) => update("totalHouseholdAttending", Number(event.target.value))} /></label>
          </div>{draft.children.slice(0, requiredChildCount(draft.numberOfChildren)).map((child, index) => <StaffChildFields child={child} index={index} updateChild={updateRegistrantChild} key={child.childNumber} />)}{draft.numberOfChildren === "More than 5" && <label className="editor-long-field">Additional children information<textarea rows={4} value={draft.additionalChildrenInformation} onChange={(event) => update("additionalChildrenInformation", event.target.value)} /></label>}</div>

          <div className="editor-section"><h3>Address, church, and emergency contact</h3><div className="form-grid compact-form">
            <label>Address line 1<input value={draft.addressLine1} onChange={(event) => update("addressLine1", event.target.value)} /></label><label>Unit / apartment / suite<input value={draft.unit} onChange={(event) => update("unit", event.target.value)} /></label><label>City<input value={draft.city} onChange={(event) => update("city", event.target.value)} /></label><label>State<input maxLength={2} value={draft.state} onChange={(event) => update("state", event.target.value.toUpperCase())} /></label><label>ZIP code<input value={draft.zipCode} onChange={(event) => update("zipCode", event.target.value)} /></label><label>Household size<input type="number" min="1" step="1" value={draft.householdSize} onChange={(event) => update("householdSize", event.target.value ? Number(event.target.value) : "")} /></label><label>Church member<select value={draft.churchMember} onChange={(event) => update("churchMember", event.target.value)}><option value="">Select</option><option>Yes</option><option>No</option></select></label><span /><label>Emergency contact name<input value={draft.emergencyContactName} onChange={(event) => update("emergencyContactName", event.target.value)} /></label><label>Emergency contact phone<input type="tel" value={draft.emergencyContactPhone} onChange={(event) => update("emergencyContactPhone", event.target.value)} /></label>
          </div></div>

          <div className="editor-section"><div className="section-heading"><h3>Purpose of visit / requested services</h3><span>{draft.selectedServices.length} selected</span></div><div className="service-picker editor-service-picker">
            {services.map((service) => <button type="button" className={draft.selectedServices.includes(service) ? "selected" : ""} onClick={() => toggleService(service)} key={service}><span>{draft.selectedServices.includes(service) && <Check size={15} />}</span>{service}</button>)}
          </div></div>

          <div className="editor-section"><h3>Schedule and support</h3><div className="form-grid compact-form">
            <label className="full">Preferred check-in time<select value={draft.slotId} onChange={(event) => update("slotId", event.target.value)}><option value="">No preference</option>{slots.map((slot) => <option key={slot.id} value={slot.id} disabled={slot.status !== "OPEN" && slot.id !== draft.slotId}>{slot.label}{slot.id === draft.slotId ? " (current)" : ` — ${slot.available} places`}</option>)}</select></label>
          </div><div className="editor-checks">
            <CheckField checked={draft.foodBagsInterest} onChange={(value) => update("foodBagsInterest", value)} label="Interested in food bag support" />
            <CheckField checked={draft.backToSchoolInterest} onChange={(value) => update("backToSchoolInterest", value)} label="Interested in back-to-school support" />
            <CheckField checked={draft.generalConsent} onChange={(value) => update("generalConsent", value)} label="General registration and selected-service consent (turning this off marks consent withdrawn and prevents check-in)" />
            <CheckField checked={draft.emailConsent} onChange={(value) => update("emailConsent", value)} label="Email confirmation, reminders, and available health report" />
            <CheckField checked={draft.smsConsent} onChange={(value) => update("smsConsent", value)} label="SMS event reminders" />
          </div></div>

          <div className="editor-save"><label>Reason for change<textarea rows={3} placeholder="Example: Registrant changed purpose of visit at check-in" value={changeReason} onChange={(event) => setChangeReason(event.target.value)} /></label><button className="button button-primary" type="button" disabled={saving || !changeReason.trim()} onClick={save}><Save size={18} /> {saving ? "Saving…" : "Save registration changes"}</button></div>

          {canCheckIn && (checkInResult || draft.checkInStatus === "CHECKED_IN" ? (
            <div className="editor-checkin-success" role="status">
              <CheckCircle2 size={30} />
              <div><h3>{checkInResult === "CHECKED_IN" ? "Check-in successful" : "Check-in already completed"}</h3><p>{draft.firstName} {draft.lastName} is checked in and ready to continue to the requested services.</p></div>
              <button className="button button-secondary" type="button" onClick={onReturnToCheckInDesk}>Return to Check-in desk</button>
            </div>
          ) : (
            <div className="editor-checkin-action">
              <div><h3>Ready to check in?</h3><p>{!draft.generalConsent ? "General registration consent must be confirmed before check-in." : hasUnsavedChanges ? "Save the registration changes above before completing check-in." : "Confirm the registration is correct, then check in this attendee."}</p></div>
              <button className="button button-primary" type="button" disabled={checkingIn || saving || hasUnsavedChanges || !draft.generalConsent} onClick={checkInRegistrant}><QrCode size={18} /> {checkingIn ? "Checking in…" : "Check in attendee"}</button>
            </div>
          ))}
        </section>
      )}
      {scannerOpen && <QrScannerModal onScan={acceptScannedCredential} onClose={() => setScannerOpen(false)} />}
    </div>
  );
}

function CheckInView({ config, session }: { config: RuntimeConfig; session: Session }) {
  const [token, setToken] = useState("");
  const [counter, setCounter] = useState("Main Entrance");
  const [attendee, setAttendee] = useState<Attendee | null>(null);
  const [message, setMessage] = useState("");
  const [messageKind, setMessageKind] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  function acceptScannedCredential(value: string) {
    setToken(normalizeQrCredential(value));
    setAttendee(null);
    setMessageKind("success");
    setMessage("QR code scanned. Review the counter location, then confirm check-in.");
    setScannerOpen(false);
  }

  async function checkIn() {
    setLoading(true); setMessage(""); setAttendee(null);
    try {
      const data = await api<{ attendee: Attendee; status: string }>(config, "/app/checkin", { method: "POST", body: JSON.stringify({ token: normalizeQrCredential(token), counter }) }, session);
      setAttendee(data.attendee); setMessageKind("success");
      setMessage(data.status === "ALREADY_CHECKED_IN"
        ? "This attendee was already checked in. Their saved details are shown; no duplicate check-in was created."
        : "Check-in completed successfully.");
    } catch (caught) { setMessageKind("error"); setMessage(caught instanceof Error ? caught.message : "Check-in failed."); }
    finally { setLoading(false); }
  }

  return (
    <div className="portal-content two-column-workflow">
      <section className="panel action-panel">
        <p className="eyebrow">Scan or type</p><h2>Attendee QR token</h2>
        <button className="button button-secondary wide scan-button" type="button" onClick={() => setScannerOpen(true)}><Camera size={19} /> Scan registration QR code</button>
        <div className="scan-divider"><span>or enter manually</span></div>
        <label>QR credential or confirmation link<input className="large-input" placeholder="MHF2026-…" value={token} onChange={(event) => setToken(event.target.value)} /></label>
        <label>Counter location<select value={counter} onChange={(event) => setCounter(event.target.value)}><option>Main Entrance</option><option>Walk-Up</option><option>VIP / Provider</option><option>Issue Resolution</option><option>Mobile Team</option></select></label>
        <button className="button button-primary wide" disabled={!token || loading} onClick={checkIn}><QrCode size={19} /> {loading ? "Checking…" : "Confirm check-in"}</button>
        {message && <Notice kind={messageKind} text={message} />}
      </section>
      <section className="panel attendee-preview">
        {!attendee ? <div className="empty-state"><Search size={34} /><h3>Attendee details appear here</h3><p>Identity and requested services are shown only after a valid QR token is confirmed.</p></div> : <AttendeeCard attendee={attendee} />}
      </section>
      {scannerOpen && <QrScannerModal onScan={acceptScannedCredential} onClose={() => setScannerOpen(false)} />}
    </div>
  );
}

function QrScannerModal({ onScan, onClose }: { onScan: (value: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const onScanRef = useRef(onScan);
  const [error, setError] = useState("");

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        if (!videoRef.current || !navigator.mediaDevices?.getUserMedia) throw new Error("Camera scanning is not supported on this device. Use manual entry instead.");
        const { BrowserQRCodeReader } = await import("@zxing/browser");
        if (!active || !videoRef.current) return;
        const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 200 });
        const controls = await reader.decodeFromConstraints(
          { audio: false, video: { facingMode: { ideal: "environment" } } },
          videoRef.current,
          (result, _error, controls) => {
            if (!result || !active) return;
            const value = result.getText();
            if (!isMegaHealthFairQrCredential(value)) {
              setError("This is not a Mega Health Fair registration QR code.");
              return;
            }
            controls.stop();
            onScanRef.current(value);
          },
        );
        if (!active) controls.stop();
        else controlsRef.current = controls;
      } catch (caught) {
        if (!active) return;
        const cameraError = caught as { name?: string; message?: string };
        setError(cameraError.name === "NotAllowedError"
          ? "Camera access was blocked. Allow camera permission for this site or use manual entry."
          : cameraError.message || "The camera could not be started. Use manual entry instead.");
      }
    }

    startCamera();
    return () => {
      active = false;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, []);

  return (
    <div className="modal-backdrop scanner-backdrop" onClick={onClose}>
      <section className="modal qr-scanner-modal" role="dialog" aria-modal="true" aria-labelledby="qr-scanner-title" onClick={(event) => event.stopPropagation()}>
        <header><div><p className="eyebrow">Check-in desk</p><h2 id="qr-scanner-title">Scan registration QR code</h2></div><button type="button" aria-label="Close scanner" onClick={onClose}><X size={20} /></button></header>
        <div className="scanner-video-wrap"><video ref={videoRef} muted playsInline /><div className="scanner-frame" aria-hidden="true" /></div>
        <p className="scanner-help">Hold the attendee’s generated QR code inside the frame. Detection is automatic.</p>
        {error && <Notice kind="error" text={error} />}
        <button className="button button-ghost wide" type="button" onClick={onClose}>Cancel and enter manually</button>
      </section>
    </div>
  );
}

function StationView({ config, session }: { config: RuntimeConfig; session: Session }) {
  const [station, setStation] = useState("Blood Pressure Check");
  const [visits, setVisits] = useState<Visit[]>([]);
  const [attendeeId, setAttendeeId] = useState("");
  const [attendeeName, setAttendeeName] = useState("");
  const [selected, setSelected] = useState<Visit | null>(null);
  const [findings, setFindings] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [message, setMessage] = useState("");

  async function refresh() {
    try {
      const data = await api<{ visits: Visit[] }>(config, `/app/stations/${encodeURIComponent(station)}/visits`, {}, session);
      setVisits(data.visits);
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Unable to load queue."); }
  }
  useEffect(() => { refresh(); }, [station]);

  async function arrive() {
    try {
      await api(config, "/app/stations/arrive", { method: "POST", body: JSON.stringify({ station, attendeeId, attendeeName }) }, session);
      setAttendeeId(""); setAttendeeName(""); setMessage("Attendee added to the station queue."); await refresh();
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Unable to add attendee."); }
  }

  async function submitFindings(complete = false) {
    if (!selected) return;
    try {
      if (!selected.status.includes("IN_PROGRESS")) {
        await api(config, `/app/visits/${selected.visitId}/findings`, { method: "POST", body: JSON.stringify({ findings, recommendations, signatureConfirmed: true, station }) }, session);
      }
      if (complete) await api(config, `/app/visits/${selected.visitId}/complete`, { method: "POST", body: JSON.stringify({ station }) }, session);
      setMessage(complete ? "Visit completed and locked." : "Findings submitted securely."); setSelected(null); setFindings(""); setRecommendations(""); await refresh();
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Unable to update visit."); }
  }

  return (
    <div className="portal-content">
      <section className="panel queue-toolbar">
        <label>Active station<select value={station} onChange={(event) => setStation(event.target.value)}>{services.map((name) => <option key={name}>{name}</option>)}</select></label>
        <div className="quick-arrive"><input placeholder="Attendee ID" value={attendeeId} onChange={(event) => setAttendeeId(event.target.value)} /><input placeholder="Attendee name" value={attendeeName} onChange={(event) => setAttendeeName(event.target.value)} /><button className="button button-primary" disabled={!attendeeId || !attendeeName} onClick={arrive}><ClipboardPlus size={18} /> Arrive</button></div>
      </section>
      {message && <Notice kind={message.includes("Unable") ? "error" : "success"} text={message} />}
      <div className="queue-board">
        {(["ARRIVED", "IN_PROGRESS", "COMPLETED"] as const).map((status) => (
          <section className="queue-column" key={status}><header><h3>{status.replace("_", " ")}</h3><span>{visits.filter((visit) => visit.status === status).length}</span></header>
            {visits.filter((visit) => visit.status === status).map((visit) => <button className="visit-card" key={visit.visitId} onClick={() => setSelected(visit)}><strong>{visit.attendeeName}</strong><span>{visit.attendeeId}</span><small>{new Date(visit.arrivalTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</small></button>)}
          </section>
        ))}
      </div>
      {selected && selected.status !== "COMPLETED" && <div className="modal-backdrop" onClick={() => setSelected(null)}><section className="modal" onClick={(event) => event.stopPropagation()}><p className="eyebrow">{selected.attendeeId}</p><h2>{selected.attendeeName}</h2><label>Clinical findings<textarea rows={5} value={findings} onChange={(event) => setFindings(event.target.value)} /></label><label>Recommendations / referrals<textarea rows={4} value={recommendations} onChange={(event) => setRecommendations(event.target.value)} /></label><div className="modal-actions"><button className="button button-ghost" onClick={() => setSelected(null)}>Cancel</button><button className="button button-secondary" disabled={!findings} onClick={() => submitFindings(false)}>Save findings</button><button className="button button-primary" disabled={!findings && selected.status !== "IN_PROGRESS"} onClick={() => submitFindings(true)}>Complete visit</button></div></section></div>}
    </div>
  );
}

function DistributionView({ config, session, groups }: { config: RuntimeConfig; session: Session; groups: string[] }) {
  const [form, setForm] = useState({ attendeeId: "", type: "FOOD_BAG", item: "Standard Food Bag", quantity: 1, override: false, overrideReason: "" });
  const [message, setMessage] = useState("");
  const isAdmin = groups.includes("EventAdmins");
  async function submit() {
    try {
      await api(config, "/app/distributions", { method: "POST", body: JSON.stringify(form) }, session);
      setMessage("Distribution recorded with duplicate protection."); setForm((current) => ({ ...current, attendeeId: "", override: false, overrideReason: "" }));
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Unable to record distribution."); }
  }
  return <div className="portal-content two-column-workflow"><section className="panel action-panel"><p className="eyebrow">Scan and release</p><h2>Record distribution</h2><label>Attendee ID<input value={form.attendeeId} onChange={(event) => setForm({ ...form, attendeeId: event.target.value })} /></label><label>Support type<select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value, item: event.target.value === "FOOD_BAG" ? "Standard Food Bag" : "Supplies Kit" })}><option value="FOOD_BAG">Food Bag</option><option value="BACK_TO_SCHOOL">Back-to-School</option></select></label><label>Item<input value={form.item} onChange={(event) => setForm({ ...form, item: event.target.value })} /></label><label>Quantity<input type="number" min="1" value={form.quantity} onChange={(event) => setForm({ ...form, quantity: Number(event.target.value) })} /></label>{isAdmin && <label className="inline-check"><input type="checkbox" checked={form.override} onChange={(event) => setForm({ ...form, override: event.target.checked })} /> Administrative duplicate override</label>}{form.override && <label>Required override reason<textarea value={form.overrideReason} onChange={(event) => setForm({ ...form, overrideReason: event.target.value })} /></label>}<button className="button button-primary wide" disabled={!form.attendeeId || (form.override && !form.overrideReason)} onClick={submit}><PackageCheck size={18} /> Record distribution</button>{message && <Notice kind={message.includes("recorded") ? "success" : "error"} text={message} />}</section><section className="panel"><div className="empty-state"><ShieldCheck size={34} /><h3>Duplicate prevention is active</h3><p>Each attendee can receive one distribution of each type. Administrative exceptions require a reason and are written to the immutable audit trail.</p></div></section></div>;
}

function IncidentView({ config, session }: { config: RuntimeConfig; session: Session }) {
  const [form, setForm] = useState({ incidentType: "Safety", severity: "LOW", attendeeId: "", description: "" });
  const [message, setMessage] = useState("");
  async function submit() {
    try { await api(config, "/app/incidents", { method: "POST", body: JSON.stringify(form) }, session); setMessage("Incident logged and routed for review."); setForm({ ...form, attendeeId: "", description: "" }); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Unable to log incident."); }
  }
  return <div className="portal-content two-column-workflow"><section className="panel action-panel"><p className="eyebrow">Confidential</p><h2>New incident</h2><label>Incident type<select value={form.incidentType} onChange={(event) => setForm({ ...form, incidentType: event.target.value })}><option>Medical</option><option>Safety</option><option>Security</option><option>Lost Child</option><option>Conflict</option><option>Facilities</option><option>Other</option></select></label><label>Severity<select value={form.severity} onChange={(event) => setForm({ ...form, severity: event.target.value })}><option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option><option value="CRITICAL">Critical</option></select></label><label>Attendee ID (optional)<input value={form.attendeeId} onChange={(event) => setForm({ ...form, attendeeId: event.target.value })} /></label><label>Description<textarea rows={6} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label><button className="button button-primary wide" disabled={!form.description} onClick={submit}><AlertTriangle size={18} /> Submit incident</button>{message && <Notice kind={message.includes("logged") ? "success" : "error"} text={message} />}</section><section className="panel"><div className="empty-state"><AlertTriangle size={34} /><h3>Escalation paths</h3><p>Critical safety, security, and medical incidents route to event administrators. Clinical text remains excluded from broad dashboards.</p></div></section></div>;
}

function StaffChildFields({ child, index, updateChild }: { child: ChildRegistration; index: number; updateChild: (index: number, key: keyof ChildRegistration, value: string | number) => void }) {
  return <fieldset className="staff-child-card"><legend>Child {child.childNumber}</legend><div className="form-grid compact-form"><label>First name<input value={child.firstName} onChange={(event) => updateChild(index, "firstName", event.target.value)} /></label><label>Last name<input value={child.lastName} onChange={(event) => updateChild(index, "lastName", event.target.value)} /></label><label>Age group<select value={child.ageGroup} onChange={(event) => updateChild(index, "ageGroup", event.target.value)}>{childAgeGroupOptions.map((item) => <option key={item}>{item}</option>)}</select></label><label>Sex at birth<select value={child.sexAtBirth} onChange={(event) => updateChild(index, "sexAtBirth", event.target.value)}>{sexAtBirthOptions.map((item) => <option key={item}>{item}</option>)}</select></label><label>Race<select value={child.race} onChange={(event) => updateChild(index, "race", event.target.value)}><option value="">Select race</option>{raceOptions.map((item) => <option key={item}>{item}</option>)}</select></label>{child.race === "Other" && <label>If other race, specify<input value={child.otherRace} onChange={(event) => updateChild(index, "otherRace", event.target.value)} /></label>}<label>Ethnicity<select value={child.ethnicity} onChange={(event) => updateChild(index, "ethnicity", event.target.value)}><option value="">Select ethnicity</option>{ethnicityOptions.map((item) => <option key={item}>{item}</option>)}</select></label>{child.ethnicity === "Other (Please Specify)" && <label>If other ethnicity, specify<input value={child.otherEthnicity} onChange={(event) => updateChild(index, "otherEthnicity", event.target.value)} /></label>}</div></fieldset>;
}

function CheckField({ checked, onChange, label, required = false }: { checked: boolean; onChange: (value: boolean) => void; label: string; required?: boolean }) {
  return <label className="editor-check"><input type="checkbox" required={required} checked={checked} onChange={(event) => onChange(event.target.checked)} /><span>{label}</span></label>;
}

function Metric({ icon, label, value, trend }: { icon: React.ReactNode; label: string; value: number; trend: string }) { return <article className="metric-card"><div>{icon}</div><span>{label}</span><strong>{value.toLocaleString()}</strong><small>{trend}</small></article>; }
function StatusRow({ label, value, good }: { label: string; value: string; good: boolean }) { return <div className="status-row"><span className={good ? "good" : "warn"}>{good ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}</span><strong>{label}</strong><em>{value}</em></div>; }
function Loading() { return <div className="loading-state"><span /><p>Loading secure event data…</p></div>; }
function Notice({ kind, text }: { kind: "success" | "error"; text: string }) { return <div className={`notice ${kind}`} role="status">{kind === "success" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}{text}</div>; }
function AttendeeCard({ attendee }: { attendee: Attendee }) {
  return (
    <div className="attendee-card">
      <div className="attendee-avatar">{attendee.displayName.split(" ").map((part) => part[0]).join("").slice(0, 2)}</div>
      {attendee.checkInStatus === "CHECKED_IN" && <div className="attendee-status"><CheckCircle2 size={16} /> Checked in</div>}
      <p className="eyebrow">{attendee.attendeeId}</p>
      <h2>{attendee.displayName}</h2>
      <div className="identity-grid"><span>Phone ending <strong>{attendee.phoneLast4}</strong></span><span>Language <strong>{attendee.preferredLanguage}</strong></span><span>Arrival <strong>{attendee.preferredCheckInTime}</strong></span></div>
      <h3>Requested services</h3>
      <div className="tag-list">{attendee.selectedServices.map((service) => <span key={service}>{service}</span>)}</div>
      {(attendee.foodBagsInterest || attendee.backToSchoolInterest) && <div className="support-callout">Family support: {[attendee.foodBagsInterest && "Food Bag", attendee.backToSchoolInterest && "Back-to-School"].filter(Boolean).join(" + ")}</div>}
    </div>
  );
}
